// Pure camera / look math: sensitivity scaling, roll, landing dip, field of view.

import { DEG2RAD, clamp, clamp01, lerp } from "./angles";

// ---- Look sensitivity ----

/** Degrees of view rotation per mouse count at CS sensitivity 1. */
export const DEG_PER_COUNT_UNIT = 0.022;

export interface LookSettings {
  sensitivity: number;
  adsSensitivityMult: number;
  scopeSensitivityMult: number;
  invertY: boolean;
}

/**
 * Degrees per mouse count: `sensitivity * 0.022`, times the zoom ratio of the view and the ADS
 * multiplier on top (the scope multiplier replaces it while a scope overlay is up), both blended in
 * with `adsFrac`. The zoom ratio is CS's `zoom_sensitivity_ratio` 1 / Valorant style: the turn per
 * count shrinks with the half-angle tangent of the zoomed view (`zoomTangent`), so the crosshair
 * crosses the screen at the hip speed whatever the zoom (a 1.25x sight turns 0.8 times as far per
 * count, the 6x sniper scope 0.17 times).
 */
export function degreesPerCount(
  s: LookSettings,
  adsFrac: number,
  adsFovMult: number,
  scoped: boolean,
): number {
  const target = scoped ? s.scopeSensitivityMult : s.adsSensitivityMult;
  return (
    s.sensitivity *
    DEG_PER_COUNT_UNIT *
    zoomTangent(adsFrac, adsFovMult) *
    lerp(1, target, clamp01(adsFrac))
  );
}

// ---- Roll ----

export const WALLRUN_ROLL_DEG = 10;
export const SLIDE_ROLL_DEG = 4;
export const STRAFE_ROLL_DEG = 1.5;
/** Time constant of the roll smoothing. */
export const ROLL_TAU_SEC = 0.09;

export interface RollInput {
  /** -1 wall on the left, 1 on the right, 0 none. */
  wallrunSide: number;
  sliding: boolean;
  /** Sideways speed relative to the view (m/s, + = right). */
  lateralSpeed: number;
  /** Yaw turn rate (rad/s, + = turning left). */
  yawRate: number;
  runSpeed: number;
}

/**
 * Target camera roll in radians. Positive roll tilts the head to the left (three.js rotation.z).
 * Wall running rolls away from the wall; slides lean into the slide's lateral motion / turn;
 * strafing leans slightly into the strafe.
 */
export function rollTarget(r: RollInput): number {
  if (r.wallrunSide !== 0) return r.wallrunSide * WALLRUN_ROLL_DEG * DEG2RAD;
  const lat = clamp(r.lateralSpeed / Math.max(0.1, r.runSpeed), -1, 1);
  if (r.sliding) {
    const turn = clamp(r.yawRate / 4, -1, 1);
    return clamp(-lat * 2 + turn, -1, 1) * SLIDE_ROLL_DEG * DEG2RAD;
  }
  return -lat * STRAFE_ROLL_DEG * DEG2RAD;
}

/** Exponential approach: moves `cur` toward `target` with time constant `tau` over `dt`. */
export function approachExp(
  cur: number,
  target: number,
  tau: number,
  dt: number,
): number {
  return target + (cur - target) * Math.exp(-dt / tau);
}

// ---- Landing dip ----

/** Camera drop (m) for a landing at `speed` m/s into the ground. */
export function landingDipAmount(speed: number): number {
  return clamp((speed - 3) * 0.012, 0, 0.14);
}

/**
 * Damped spring for the landing dip. `offset` is the vertical camera offset in metres
 * (negative = lower); it is kicked down on landing and settles back with a small overshoot.
 */
export class LandingDip {
  offset = 0;
  private vel = 0;
  private static readonly K = 320;
  private static readonly C = 2 * 0.62 * Math.sqrt(LandingDip.K);

  land(speed: number): void {
    const amount = landingDipAmount(speed);
    if (amount > 0) this.offset = Math.min(this.offset, -amount);
  }

  step(dt: number): number {
    // Substeps keep the stiff spring stable on long frames.
    let left = Math.min(dt, 0.1);
    while (left > 0) {
      const h = Math.min(left, 1 / 120);
      this.vel += (-LandingDip.K * this.offset - LandingDip.C * this.vel) * h;
      this.offset += this.vel * h;
      left -= h;
    }
    if (Math.abs(this.offset) < 1e-5 && Math.abs(this.vel) < 1e-4) {
      this.offset = 0;
      this.vel = 0;
    }
    return this.offset;
  }

  reset(): void {
    this.offset = 0;
    this.vel = 0;
  }
}

// ---- Field of view ----

export const DYNAMIC_FOV_MAX_DEG = 8;

/** Extra horizontal FOV (degrees): 0 at run speed up to `DYNAMIC_FOV_MAX_DEG` at the hop speed cap. */
export function dynamicFovBonus(
  speed: number,
  runSpeed: number,
  hopCap: number,
): number {
  if (hopCap <= runSpeed) return 0;
  return (
    clamp01((speed - runSpeed) / (hopCap - runSpeed)) * DYNAMIC_FOV_MAX_DEG
  );
}

/** Time constant of the FOV pulse that marks a movement event. */
export const FOV_PULSE_TAU_SEC = 0.16;
/** Most that pulses add up to (degrees). */
export const FOV_PULSE_MAX_DEG = 4;
/** FOV pulse (degrees of horizontal FOV) per movement event: the camera widens and settles back. */
export const FOV_PULSE_DEG = {
  perfectHop: 1.5,
  slideReset: 2.5,
  wallJump: 2,
  climb: 1,
  ledgeRecover: 2,
} as const;

/**
 * Factor on the half-angle tangent of the view at `adsFrac`: `adsFovMult` is the weapon's value at
 * full ADS (iron sights and the red dot 0.8 = 1.25x, the 6x sniper scope 0.17).
 */
export function zoomTangent(adsFrac: number, adsFovMult: number): number {
  return lerp(1, clamp(adsFovMult, 0.05, 1.5), clamp01(adsFrac));
}

/** Horizontal FOV after ADS zoom (`zoomTangent` applied to the half-angle tangent). */
export function zoomedHFov(
  hFovDeg: number,
  adsFrac: number,
  adsFovMult: number,
): number {
  const half =
    Math.tan((hFovDeg * DEG2RAD) / 2) * zoomTangent(adsFrac, adsFovMult);
  return (2 * Math.atan(half)) / DEG2RAD;
}
